import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Paper, TextField, Typography,
} from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { ENDPOINTS, apiCall } from '../utils/api.js';

const SUPPORT_EMAIL = 'support@sqipqueue.com';

/**
 * Org-admin subscription cancellation (billing page).
 * Shows what cancelling means for the current plan, requires typing CANCEL to
 * confirm, calls the cancel endpoint, then shows a confirmation with next steps.
 */
export default function CancelSubscriptionCard({ orgId, sub, onCancelled }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // response after a successful cancel

  if (!sub || sub.status === 'canceled') return null;

  const isTrial = sub.status === 'trialing' || sub.status === 'incomplete';
  const consequence = isTrial
    ? 'Your account will be deactivated. Queue and appointment data will be retained for 30 days.'
    : 'Your subscription will end at the current billing period end date. You will retain access until then.';

  const doCancel = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await apiCall(ENDPOINTS.ORG_CANCEL_SUBSCRIPTION(orgId), { method: 'POST' });
      setResult(res);
      setOpen(false);
      if (onCancelled) onCancelled(res);
    } catch (err) {
      setError((err && (err.message || (err.body && err.body.message))) || 'Could not cancel. Please try again.');
    }
    setBusy(false);
  };

  // ── post-cancel confirmation screen ──
  if (result) {
    return (
      <Paper elevation={0} sx={{ mt: 2, p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 3, textAlign: 'center' }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 44, color: 'success.main', mb: 1 }} />
        <Typography variant="h6" fontWeight={700} gutterBottom>Cancellation confirmed</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto' }}>
          {result.message}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          A confirmation email is on its way. Changed your mind or need help?{' '}
          Contact us at <strong>{SUPPORT_EMAIL}</strong>.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ mt: 2, p: { xs: 2, sm: 2.5 }, border: '1px solid', borderColor: 'rgba(211,47,47,0.35)', borderRadius: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} color="error.main" sx={{ mb: 0.5 }}>
        Cancel subscription
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Current plan: <strong>{sub.plan_label || (isTrial ? 'Free trial' : 'Paid')}</strong>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {consequence}
      </Typography>
      <Button
        variant="outlined" color="error" startIcon={<CancelOutlinedIcon />}
        onClick={() => { setConfirmText(''); setError(''); setOpen(true); }}
        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
      >
        Cancel subscription
      </Button>

      <Dialog open={open} onClose={() => !busy && setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Are you sure you want to cancel?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>{consequence}</Alert>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Type <strong>CANCEL</strong> to confirm.
          </Typography>
          <TextField
            fullWidth size="small" value={confirmText} autoFocus
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="CANCEL"
          />
          {error && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={busy} sx={{ textTransform: 'none' }}>
            Keep my subscription
          </Button>
          <Button
            variant="contained" color="error"
            disabled={busy || confirmText.trim().toUpperCase() !== 'CANCEL'}
            onClick={doCancel}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {busy ? <CircularProgress size={18} color="inherit" /> : 'Cancel subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
